import { Alert } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { ElevatorPreviewPanel } from '~/components/compliance/ElevatorPreviewPanel';
import { buildCarColumns } from '~/components/portfolio/propertyCarColumns';
import { openInNewTab } from '~/components/rowPreview/RowPreviewPanel';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { LiftAITable } from '~/components/table';
import type { CarInProperty } from '~/data/schemas/car';
import type { Invoice } from '~/data/schemas/invoice';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { usePropertyCarsKpis } from '~/hooks/usePropertyCarsKpis';
import { buildUrlForElevator } from '~/utils/urlBuilder';

const NO_ACTIONS: never[] = [];

/**
 * The elevators this invoice serviced, rendered with the property Elevators tab's real
 * column set. `invoice.carsServiced` carries only `{id, name}`, so the KPI columns come
 * from the property's elevator list narrowed to the serviced ids. A serviced car missing
 * from that list is called out rather than silently dropped.
 */
export function ElevatorsServicedSection({ invoice }: { invoice: Invoice }) {
  const tableHeight = useDetailTableHeight();
  const apiRef = useGridApiRef();
  const preview = useTableRowPreview({
    list: 'invoice_elevators',
    apiRef,
    canPreview: (rowId) => Boolean(apiRef.current?.getRow<CarInProperty>(rowId)?.id),
  });
  const terms = useLocaleTerms();
  const [searchParams] = useSearchParams();
  const propertyId = invoice.property?.id;
  const { cars = [], loading } = usePropertyCarsKpis(propertyId ?? null);

  const servicedIds = useMemo(
    () => new Set(invoice.carsServiced.map((car) => car.id)),
    [invoice.carsServiced],
  );

  const rows = useMemo(
    () => cars.filter((car) => car.id != null && servicedIds.has(car.id)),
    [cars, servicedIds],
  );

  const carDetailPath = useCallback(
    (carId: string) => buildUrlForElevator(carId, searchParams),
    [searchParams],
  );

  const columns = useMemo(
    () =>
      buildCarColumns({
        propertyId: propertyId ?? '',
        canEdit: false,
        getActionsEl: () => NO_ACTIONS,
        terms,
        carDetailPath,
      }),
    [propertyId, terms, carDetailPath],
  );

  // The row opens the unit in a new tab, leaving the invoice it was serviced on
  // in place — the point of this list is what the invoice charged for.
  const rowHref = useCallback(
    (car: CarInProperty) => (car.id ? carDetailPath(car.id) : undefined),
    [carDetailPath],
  );

  const missingCount = invoice.carsServiced.length - rows.length;

  if (!propertyId) {
    return <Alert severity="info">This invoice has no property, so it has no elevators.</Alert>;
  }

  return (
    <>
      {!loading && missingCount > 0 ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {missingCount} of {invoice.carsServiced.length} serviced {terms.vehicles.toLowerCase()}{' '}
          could not be shown — they are no longer on this property, or fall outside the selected
          date range.
        </Alert>
      ) : null}
      <LiftAITable<CarInProperty>
        height={tableHeight}
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        rowCount={rows.length}
        loading={loading}
        tableHeaderTitle={terms.vehiclesServiced}
        rowHref={rowHref}
        toolbarProps={{ filterControls: preview.peekSwitch }}
        {...preview.tableProps}
        getRowId={(row) => row.id ?? row.ahjId}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
              capacityLbs: false,
              controllerModel: false,
              driveType: false,
              lastSspiTest: false,
              numEntrances: false,
              numLandings: false,
              oem: false,
              speedFpm: false,
            },
          },
        }}
      />
      {preview.enabled ? (
        <ElevatorPreviewPanel
          rowPreview={preview.rowPreview}
          onOpenFullPage={(carId) => openInNewTab(carDetailPath(carId))}
        />
      ) : null}
    </>
  );
}
